import { useRouter } from "@app/app/components/router/RouterContext";
import { useAppState, useBookingEngineStateContext, useCustomNavigate, usePropertyContext } from "@app/app/hooks";
import { log } from "@app/app/services/log";
import { storageManager } from "@app/app/services/storageManager";
import { isOneOfRoutePagesActive, isRoutePageActive } from "@app/app/utils/appUtils";
import { generateUniqueBookingSelectionHash } from "@app/app/utils/url/generateUniqueBookingSelectionHash";
import { isAvailabilitySearchParamsValidForRequestType } from "@app/app/utils/url/isAvailabilitySearchParamsValidForRequestType";
import { AvailabilityRequestType } from "@app/availability/constants/AvailabilityRequestType";
import { PaymentIntentCaptureMethod } from "@app/payment/constants/PaymentIntentCaptureMethod";
import { PaymentProviderClient } from "@app/payment/constants/PaymentProviderClient";
import { PaymentTypes } from "@app/payment/constants/PaymentTypes";
import { TPaymentIntentModel } from "@app/payment/types";
import { getCleanedNavigateTarget, isPaymentAccepted } from "@app/payment/utils/paymentUtils";
import { UtilsDom, UtilsUrl } from "@hotelchamp/common";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { CheckoutPageKeys } from "../constants/CheckoutPageKeys";
import { CheckoutSessionDefaults } from "../constants/CheckoutSessionDefaults";
import { CheckoutSessionProviderMode } from "../constants/CheckoutSessionProviderMode";
import { CheckoutSessionStatus } from "../constants/CheckoutSessionStatus";
import { RestorableStateTypes } from "../constants/RestoredStateTypes";
import { StorageKeys } from "../constants/StorageKeys";
import {
    CheckoutSessionContext,
    ICheckoutSessionContext,
    ICheckoutSessionProviderReviveSessionProps,
} from "../context/CheckoutSessionContext";
import { useUpsertCheckoutSession } from "../hooks";
import { useCheckoutBookingValidation } from "../hooks/useCheckoutBookingValidation";
import { useCheckoutSessionStatePersistance } from "../hooks/useCheckoutSessionStatePersistance";
import { ICheckoutSessionState } from "../types";
import { defaultEmptyCheckoutSessionStateCreator, isValidActiveCheckoutSessionState } from "../utils/checkoutSessionUtils";

export type TCheckoutSessionProviderChildrenRenderProp = (context: ICheckoutSessionContext) => React.ReactNode | React.ReactNode[];

export interface ICheckoutSessionProviderProps {
    children?: React.ReactNode | React.ReactNode[] | TCheckoutSessionProviderChildrenRenderProp;
    mode?: CheckoutSessionProviderMode;
}

const DEFAULT_CAPTURE_METHOD = PaymentIntentCaptureMethod.AutomaticAsync;

/**
 * @note - When debugging session id's and id changes more often, possible reasons:
 *  - The state is initialized with empty state and a new session id (before restored from localStorage)
 *  - The state will asynchronousely restored from localStorage, if present, which could update initial session id
 *  - After state is initialized and restored, change in the cart could cause a change of capture_method which results in
 *      creating a new checkout session and thus a new session id
 * @returns
 */
export function CheckoutSessionProvider({ mode = CheckoutSessionProviderMode.Checkout, children }: ICheckoutSessionProviderProps) {
    const { globalState: globalAppState, visitedPageHistory } = useAppState();
    const visitorId = globalAppState.visitorId!;
    const { activeProperty, activePropertyId } = usePropertyContext();
    const { route } = useRouter();

    const {
        getAvailabilityParams,
        initialized: isBookingEngineStateInitialized,
        isCartInitialized,
        resetBooking,
        getState: getBookingEngineState,
        shouldSummaryPageBeShownForPageView,
        setShouldSummaryPageBeShownForPageView,
    } = useBookingEngineStateContext();

    const bookingEngineState = getBookingEngineState();
    const bookingHash = generateUniqueBookingSelectionHash(bookingEngineState);

    const { isPersisting, isRestoring, persistState, restoreState, clearState } = useCheckoutSessionStatePersistance({
        visitorId,
        activePropertyId,
        bookingHash,
    });

    const [state, setState] = useState<ICheckoutSessionState>(
        defaultEmptyCheckoutSessionStateCreator({
            visitor_id: visitorId,
            property_id: activePropertyId,
            booking_hash: bookingHash,
        })
    );

    const [isCheckoutSessionInitializing, setIsCheckoutSessionInitializing] = useState(true);
    const [, setActiveAllowedPaymentTypes] = useState<PaymentTypes[]>([]);
    const navigate = useCustomNavigate();

    const [activePaymentType, setActivePaymentType] = useState<PaymentTypes | undefined>(state.payment_type);
    const availabilitySearchQuery = getAvailabilityParams(AvailabilityRequestType.Check);
    const isAvailabilityRequestSearchQueryValidForCheckout = isAvailabilitySearchParamsValidForRequestType(availabilitySearchQuery);
    const upsertCheckoutSessionMutator = useUpsertCheckoutSession(activePropertyId!, availabilitySearchQuery.toString());
    const isDirectActivePaymentType = activePaymentType === PaymentTypes.Direct;
    const isOffSessionActivePaymentType = activePaymentType === PaymentTypes.OffSession;
    const isCreditCardGuaranteePaymentType = activePaymentType === PaymentTypes.CreditCardGuarantee;
    const hasActiveCheckoutSession =
        (isDirectActivePaymentType && !!state.external_payment_intent_id && !!state.external_payment_intent_secret) ||
        (isOffSessionActivePaymentType && !!state.external_setup_intent_id && !!state.external_setup_intent_secret) ||
        isCreditCardGuaranteePaymentType;
    const [isPaymentConfirming, setIsPaymentConfirming] = useState(false);
    const bookingId = state.booking_id;
    const checkoutBookingValidation = useCheckoutBookingValidation({
        propertyId: activePropertyId,
        bookingId,
    });
    const paymentIntentModelProviderClient = checkoutBookingValidation.data?.paymentIntentModel?.payment_provider?.client;
    const currentCheckoutSessionId = state.session_id;
    const hasValidActiveCheckoutSessionInMemory = isValidActiveCheckoutSessionState(state);
    const hasPersistedCheckoutSessionStateBeenInitialized = useRef(false);

    // set state in storage on change
    // normally setCheckoutSessionState should be used over setState because it will set it in localStorage,
    // but this is to make sure it won't get out of sync, with the cost of the additional overhead
    useEffect(() => {
        if (!isCheckoutSessionInitializing) {
            persistState(state);
        }
    }, [state, isCheckoutSessionInitializing, persistState]);

    useEffect(() => {
        if (!!state.payment_type && activeProperty?.allowed_payment_types && Array.isArray(activeProperty?.allowed_payment_types)) {
            const nextActiveAllowedPaymentTypes = activeProperty?.allowed_payment_types || [];
            const isActivePaymentTypeAllowed = nextActiveAllowedPaymentTypes.includes(state.payment_type);

            setActiveAllowedPaymentTypes(activeProperty.allowed_payment_types);

            if (nextActiveAllowedPaymentTypes.length && !isActivePaymentTypeAllowed) {
                // clear checkout session when allowed payment types has been changed during a checkout session

                clearAllState({ payment_type: nextActiveAllowedPaymentTypes[0] }).then(() => {
                    alert(
                        "It seems that the allowed payment types have been changed during the checkout session. The state will be cleared and the page will be reloaded"
                    );

                    document.location.reload();
                });
            }
        }
    }, [activeProperty?.allowed_payment_types, state.payment_type]);

    useEffect(() => {
        // Responsible to set shouldSummaryPageBeShownForPageView to false when the current page is CheckoutReturn to prevent
        // that the customer is redirected to the summary page when payment attempt failed and user want to try again
        // through checkout entry page
        if (isOneOfRoutePagesActive([CheckoutPageKeys.CheckoutReturn])) {
            setShouldSummaryPageBeShownForPageView(false);
        }
    }, []);

    const navigateToCleanedTarget = useCallback(
        (params: { p: string; c: string }, paymentProviderClient?: PaymentProviderClient) => {
            navigate(getCleanedNavigateTarget(params, paymentProviderClient), undefined, { replaceState: true });
        },
        [navigate]
    );

    const reInitializePersistedCheckoutSessionState = useCallback(
        async ({ isInitial = false }: { isInitial?: boolean } = {}) => {
            const restoredResult = await restoreState();
            const isRestorable = restoredResult?.type === RestorableStateTypes.Restorable;
            const isMismatch = restoredResult?.type === RestorableStateTypes.Mismatch;
            const previousPage = visitedPageHistory[visitedPageHistory.length - 1];
            const isPreviousPageTheCheckoutReturn = [CheckoutPageKeys.CheckoutReturn].includes(previousPage?.page as CheckoutPageKeys);
            const isCurrentPageTheCheckoutEntry = isRoutePageActive(CheckoutPageKeys.CheckoutEntry);

            if (isRestorable) {
                const nextState = { ...restoredResult.state! };

                setActivePaymentType(nextState.payment_type);

                setState(nextState);

                setIsCheckoutSessionInitializing(false);
            } else if (isMismatch) {
                setIsCheckoutSessionInitializing(false);

                // could not be restored since booking hash from localStorage belongs
                // to other session
                // redir to summary? and update state with new selection?
                if (!isOneOfRoutePagesActive([CheckoutPageKeys.CheckoutReturn, CheckoutPageKeys.CheckoutFinished])) {
                    resetBooking();

                    navigate("?p=base&c=search", {}, { replaceState: true });
                }
            } else if (mode === CheckoutSessionProviderMode.Checkout) {
                let nextState: ICheckoutSessionState;

                // Nothing could be restored from localStorage
                if (hasValidActiveCheckoutSessionInMemory && !isInitial) {
                    nextState = { ...state };

                    if (isRoutePageActive(CheckoutPageKeys.CheckoutEntry)) {
                        navigateToCleanedTarget(
                            {
                                p: "base",
                                c: CheckoutPageKeys.Summary,
                            },
                            paymentIntentModelProviderClient
                        );
                    }
                } else {
                    // Create new empty checkout session state
                    nextState = {
                        ...defaultEmptyCheckoutSessionStateCreator({
                            visitor_id: visitorId,
                            property_id: activePropertyId,
                            booking_hash: bookingHash,
                        }),
                        capture_method: DEFAULT_CAPTURE_METHOD,
                    };
                }

                setState(nextState);

                setIsCheckoutSessionInitializing(false);
            }
        },
        [
            setActivePaymentType,
            mode,
            activePropertyId,
            visitorId,
            bookingHash,
            navigateToCleanedTarget,
            hasValidActiveCheckoutSessionInMemory,
            setState,
            setIsCheckoutSessionInitializing,
            visitedPageHistory,
            currentCheckoutSessionId,
            paymentIntentModelProviderClient,
        ]
    );

    /**
     * Responsible to handle restoring the persisted checkout session state when the tab becomes active again.
     */
    useEffect(() => {
        const handleEnableTabEvent = () => {
            if (document.visibilityState === "visible") {
                const isCheckoutEntryPageActive = isRoutePageActive(CheckoutPageKeys.CheckoutEntry);
                const previousPage = visitedPageHistory[visitedPageHistory.length - 2];
                const isPreviousPageTheCheckoutReturn = [CheckoutPageKeys.CheckoutReturn].includes(previousPage?.page as CheckoutPageKeys);

                if (!isRestoring) {
                    reInitializePersistedCheckoutSessionState();
                }

                // When this window becomes active again AND the checkout entry page is active AND the previous visited page is not checkout-return, always redirect back to the summary page.
                // Important, don't change this behavior.
                if (isCheckoutEntryPageActive && !isPreviousPageTheCheckoutReturn) {
                    navigateToCleanedTarget(
                        {
                            p: "base",
                            c: CheckoutPageKeys.Summary,
                        },
                        paymentIntentModelProviderClient
                    );
                }
            }
        };

        const detachers = [UtilsDom.on(window, "visibilitychange", handleEnableTabEvent)];

        return () => detachers.forEach((detacher) => detacher());
    }, [
        isRestoring,
        restoreState,
        reInitializePersistedCheckoutSessionState,
        paymentIntentModelProviderClient,
        shouldSummaryPageBeShownForPageView,
    ]);

    // restore state from storage initially when component is mounted
    useEffect(() => {
        if (isBookingEngineStateInitialized && isCartInitialized && !hasPersistedCheckoutSessionStateBeenInitialized.current) {
            hasPersistedCheckoutSessionStateBeenInitialized.current = true;

            reInitializePersistedCheckoutSessionState();
        }
    }, [isBookingEngineStateInitialized, isCartInitialized, reInitializePersistedCheckoutSessionState]);

    const upsertCheckoutSession = useCallback(async (): Promise<ICheckoutSessionState> => {
        if (activePropertyId) {
            const nextCheckoutSessionState = await upsertCheckoutSessionMutator.mutateAsync(state);

            if (nextCheckoutSessionState.session_id) {
                setState(nextCheckoutSessionState);

                return nextCheckoutSessionState;
            }

            return nextCheckoutSessionState;
        } else {
            throw new Error("No property id set");
        }
    }, [upsertCheckoutSessionMutator, activePropertyId, availabilitySearchQuery]);

    const clearCheckoutSession = useCallback(
        async (forcedState?: Partial<ICheckoutSessionState>): Promise<void> => {
            await clearState();

            const nextState = {
                ...defaultEmptyCheckoutSessionStateCreator({
                    visitor_id: visitorId,
                    property_id: activePropertyId,
                    booking_hash: bookingHash,
                }),
                ...(forcedState || {}),
            };

            setState(nextState);
        },
        [bookingHash, activePropertyId, visitorId, clearState, setState]
    );

    const clearAllState = async (forcedState?: Partial<ICheckoutSessionState>) => {
        clearCheckoutSession(forcedState);

        await Promise.all(storageManager.getAll().map((storage) => storage.clearAll())).then(() => {
            console.warn("All storages have been cleared");
        });

        resetBooking();

        setTimeout(() => {
            const nextUrl = UtilsUrl.setUrlParams(document.location.href, {
                p: "base",
                c: "search",
            });

            document.location.href = nextUrl;
        });
    };

    (window as any).clearAllState = clearAllState;

    const setCheckoutSessionState = useCallback(
        async (nextStateProps: Partial<ICheckoutSessionState>) => {
            const nextState = {
                ...state,
                ...nextStateProps,
            };

            setState(nextState);

            await persistState(nextState);
        },
        [setState, state]
    );

    // Should be called in checkout step when cart has items
    // upsert session server side which will create a new payment intent for payment provider
    // @param guest     to force guest params from the outside to compare against
    const reviveSession = useCallback(
        async ({
            payment_type,
            forceNew,
            forceReservedBooking,
        }: ICheckoutSessionProviderReviveSessionProps = {}): Promise<ICheckoutSessionState> => {
            if (isCheckoutSessionInitializing) {
                throw Error("Cannot start session while state is initializing");
            }

            if (!activeProperty) {
                throw Error("Cannot start session without active property");
            }

            let nextPaymentType: PaymentTypes | undefined;

            const rate = getBookingEngineState()?.bookingState.bookings[0].rate;

            if (rate) {
                if (rate.payment_activated) {
                    nextPaymentType = [PaymentTypes.Direct, PaymentTypes.OffSession].includes(state.payment_type as PaymentTypes)
                        ? state.payment_type
                        : PaymentTypes.Direct;
                } else if (rate.cc_verification) {
                    nextPaymentType = PaymentTypes.CreditCardGuarantee;
                }

                if (nextPaymentType && activePaymentType !== nextPaymentType) {
                    log.debug("hiero1.5.5 - CheckoutSessionProvider::middle - CHANGED - activePaymentType", activePaymentType);

                    setActivePaymentType(nextPaymentType);
                }
            }

            const currentReservedBooking = forceReservedBooking || checkoutBookingValidation.data?.reservedBooking;
            const isBookingSelectionChanged = state.booking_hash && state.booking_hash !== bookingHash;
            const currentBookingId = currentReservedBooking?.id || state.booking_id;
            const didAttemptToEmpty = !!currentBookingId;

            const nextCaptureMethod = DEFAULT_CAPTURE_METHOD;
            const leadingPaymentType = payment_type || nextPaymentType || activePaymentType;

            const isCaptureMethodChanged = false;
            const isPaymentTypeChanged = leadingPaymentType !== state.payment_type;
            const isStatusFinished = state.status === CheckoutSessionStatus.Finished;
            const currentEmail = currentReservedBooking?.guest?.email;
            const isEmailChanged = !!state.guest?.email && state.guest?.email !== currentEmail;

            const paymentType = currentReservedBooking?.payment_type;

            let paymentIntentModel: TPaymentIntentModel | undefined;

            if (paymentType === PaymentTypes.Direct) {
                paymentIntentModel = currentReservedBooking?.payment_intent;
            } else if (paymentType === PaymentTypes.OffSession) {
                paymentIntentModel = currentReservedBooking?.payment_setup_intent;
            }

            // todo: check for pcib finished
            const hasSessionFinishedPayment = !!paymentIntentModel?.status && isPaymentAccepted(paymentIntentModel.status);

            // const isPaymentCancelled = !!paymentIntentModel?.status && paymentUtils.isPaymentCancelled(paymentIntentModel.status);

            let nextState = {
                ...state,
            };

            const shouldResetCheckoutSession =
                (isCartInitialized && isCaptureMethodChanged) ||
                // hasSessionFinishedPayment ||
                // isStatusFinished ||
                isPaymentTypeChanged ||
                (didAttemptToEmpty && isEmailChanged) ||
                isBookingSelectionChanged ||
                !!forceNew;

            if (shouldResetCheckoutSession) {
                nextState = {
                    ...defaultEmptyCheckoutSessionStateCreator({
                        visitor_id: visitorId,
                        property_id: activePropertyId,
                        booking_hash: bookingHash,
                    }),
                    capture_method: nextCaptureMethod,
                    payment_type: leadingPaymentType,
                    started_at: Date.now(),
                };

                // Keep link to current booking if any. But only if the status is not finished and we are not forcing a new session.
                // The idea is that we want to keep the booking id in the session state when booking is not finished.
                if (!isStatusFinished && !forceNew && !!currentReservedBooking?.id) {
                    nextState.booking_id = currentReservedBooking.id;
                }

                setState(nextState);
            }

            if (!upsertCheckoutSessionMutator.isLoading) {
                // Only upsert the checkout session when we're not already
                // upserting it, else we'll get stuck in an infinite loop,
                // resulting in the API throttling us.

                await upsertCheckoutSessionMutator.mutateAsync(nextState).then((nextCheckoutSessionState) => {
                    if (nextCheckoutSessionState.session_id) {
                        const nextStateUpdatedOnServer = {
                            ...nextState,
                            ...nextCheckoutSessionState,
                        };

                        // @TODO - availability_request_data is send as query string to the server.
                        // The value in the CheckoutSessionData is not used other then for updating Stripe when
                        // a value is assinged to it. The actual value doesn't matter.
                        // This should be refactored to rate id and room id.
                        // Since availability_request_data in the checkout session response is not used client side,
                        // it is removed from state to avoid confusion.
                        if ("availability_request_data" in nextStateUpdatedOnServer) {
                            delete nextStateUpdatedOnServer["availability_request_data"];
                        }

                        log.debug("hiero1.21.1 - CheckoutSessionProvider - nextStateUpdatedOnServer", nextStateUpdatedOnServer);

                        setState(nextStateUpdatedOnServer);

                        nextState = nextStateUpdatedOnServer;
                    }
                });
            }

            return nextState;
        },
        [
            isCheckoutSessionInitializing,
            activePaymentType,
            activeProperty?.id,
            state,
            bookingHash,
            checkoutBookingValidation.data?.reservedBooking?.payment_type,
            checkoutBookingValidation.data?.reservedBooking?.payment_intent,
            checkoutBookingValidation.data?.reservedBooking?.payment_setup_intent,
            isCartInitialized,
            upsertCheckoutSessionMutator,
            availabilitySearchQuery,
        ]
    );

    /**
     * When checkout session state has not been changed since 15 minutes, or summary should still be shown for current pageview
     * and the active page is the checkout entry, we redirect user to summary page just in case.
     */
    useEffect(() => {
        const isCheckoutEntryPageActive = isRoutePageActive(CheckoutPageKeys.CheckoutEntry);

        let doNavigateToSummary = false;

        if (isCheckoutEntryPageActive) {
            // when the tab is active again, we want to check if the user is allowed the be on the checkout entry page.
            // Per page view it is required that the user went through to the summary page.
            doNavigateToSummary = isCheckoutEntryPageActive && shouldSummaryPageBeShownForPageView;

            if (isBookingEngineStateInitialized && isCheckoutSessionInitializing) {
                const isCheckoutSessionTtlExpired = state?.expired_at && state.expired_at < Date.now();

                if (isCheckoutSessionTtlExpired) {
                    doNavigateToSummary = true;
                } else {
                    updateSessionExpiredAt();
                }
            }

            if (doNavigateToSummary) {
                navigateToCleanedTarget(
                    {
                        p: "base",
                        c: CheckoutPageKeys.Summary,
                    },
                    paymentIntentModelProviderClient
                );
            }
        }
    }, [
        isBookingEngineStateInitialized,
        isCheckoutSessionInitializing,
        state,
        shouldSummaryPageBeShownForPageView,
        paymentIntentModelProviderClient,
    ]);

    /**
     * Responsible to handle checkout session booking validation state changes
     */
    useEffect(() => {
        const isRequiredValidationDataLoaded =
            !checkoutBookingValidation.state.isRevalidating && !isCheckoutSessionInitializing && !isRestoring;
        const isReadyToHandleValidationStateChanges =
            !isPaymentConfirming && // if payment is confirming we don't want to redirect or validate
            isRequiredValidationDataLoaded &&
            !!checkoutBookingValidation &&
            hasActiveCheckoutSession;
        const routeBookingId = route.query?.id;
        const hasValidParamsForThankYouPage = !!routeBookingId || !!state.booking_id;

        /**
         * In case the data is loaded and the user is in the checkout flow, make sure
         * that we have valid availavility request search params to continue with the checkout flow.
         *
         * Otherwise something is off, redirect user back to search page to start over
         */
        if (isRequiredValidationDataLoaded) {
            const cannotRenderCheckoutEntryOrReturnPages =
                !isAvailabilityRequestSearchQueryValidForCheckout &&
                isOneOfRoutePagesActive([CheckoutPageKeys.CheckoutEntry, CheckoutPageKeys.CheckoutReturn]);

            const cannotRenderCheckoutThankYouPage = !hasValidParamsForThankYouPage && isRoutePageActive(CheckoutPageKeys.CheckoutFinished);

            if (cannotRenderCheckoutEntryOrReturnPages || cannotRenderCheckoutThankYouPage) {
                navigateToCleanedTarget(
                    {
                        p: "base",
                        c: "search",
                    },
                    paymentIntentModelProviderClient
                );
            }
        }

        if (isReadyToHandleValidationStateChanges) {
            const checkoutBookingValidationData = checkoutBookingValidation.data;
            const checkoutBookingValidationDataBookingId = checkoutBookingValidationData?.reservedBooking.id;
            const checkoutBookingValidationDataState = checkoutBookingValidation.state;
            const hasValidBookingIdValues = !!checkoutBookingValidationDataBookingId && !!bookingId;
            const paymentProviderClient = checkoutBookingValidationData?.paymentIntentModel?.payment_provider?.client;

            if (hasValidBookingIdValues && checkoutBookingValidationDataBookingId !== bookingId) {
                reviveSession({ forceNew: true });

                throw new Error("Mismatch between checkout session validation booking id and checkout session booking id");
            }

            // @todo - Scenario: think through - what if payment is accepted but booking state is not confirmed?
            // const isCheckoutEntryPageRoute = isRoutePageActive(CheckoutPageKeys.CheckoutEntry);

            if (checkoutBookingValidationDataState.isPaymentAccepted) {
                if (isRoutePageActive(CheckoutPageKeys.CheckoutFinished)) {
                    if (state.status !== CheckoutSessionStatus.Finished) {
                        // Clear checkout state
                        // 1. clear stored guest details
                        storageManager.getGlobalSession().clear(StorageKeys.CheckoutGuestDetailsState);

                        setState((nextState) => ({
                            ...nextState,
                            status: CheckoutSessionStatus.Finished,
                        }));
                    }
                } else {
                    /**
                     * Payment is accepted.
                     * Redirect to thank you page.
                     */
                    navigateToCleanedTarget(
                        {
                            p: "base",
                            c: CheckoutPageKeys.CheckoutFinished,
                        },
                        paymentProviderClient
                    );
                }
            } else if (checkoutBookingValidationDataState.isPaymentDeclined) {
                if (isOneOfRoutePagesActive([CheckoutPageKeys.CheckoutReturn, CheckoutPageKeys.CheckoutFinished])) {
                    /**
                     * Payment is declined.
                     * Redirect to checkout item page so the user can start a new payment.
                     * FYI: the checkout session is not cleared, so the user can continue with the same session
                     * and the same payment or setup intent
                     *
                     * Only redirect when CheckoutReturn or CheckoutFinished page is active. When user
                     * want to go to other page outside checkout flow, we don't want to redirect back to checkout entry page.
                     */
                    navigateToCleanedTarget(
                        {
                            p: "base",
                            c: CheckoutPageKeys.CheckoutEntry,
                        },
                        paymentProviderClient
                    );
                }
            } else if (checkoutBookingValidationDataState.isPaymentPending) {
                if (!isRoutePageActive(CheckoutPageKeys.CheckoutReturn)) {
                    /**
                     * Payment is pending.
                     * Redirect to checkout return page and let that page
                     * show a spinner and possibly a message to the user and the option to start a new payment.
                     *
                     * Futher this page is responsible to poll the checkout session validation and determine
                     * what to do in case of success, failure or timeout.
                     */
                    navigateToCleanedTarget(
                        {
                            p: "base",
                            c: CheckoutPageKeys.CheckoutReturn,
                        },
                        paymentProviderClient
                    );
                }
            } else if (checkoutBookingValidationDataState.isPaymentIdle) {
                if (isRoutePageActive(CheckoutPageKeys.CheckoutReturn) || isRoutePageActive(CheckoutPageKeys.CheckoutFinished)) {
                    /**
                     * Payment is idle and the user is either on the checkout return page or thank you page.
                     * Redirect to checkout entry page so the user can start a new payment.
                     */
                    navigateToCleanedTarget(
                        {
                            p: "base",
                            c: CheckoutPageKeys.CheckoutEntry,
                        },
                        paymentProviderClient
                    );
                }
            }
        } else if (isRequiredValidationDataLoaded && !isCheckoutSessionInitializing && !hasActiveCheckoutSession) {
            if (isRoutePageActive(CheckoutPageKeys.CheckoutReturn)) {
                /**
                 * No booking is present and the user is on the checkout return page.
                 * Redirect to checkout entry page so the user can start a new payment.
                 */
                navigateToCleanedTarget({
                    p: "base",
                    c: CheckoutPageKeys.CheckoutEntry,
                });
            }
        }
    }, [
        checkoutBookingValidation,
        bookingId,
        reviveSession,
        navigateToCleanedTarget,
        isAvailabilityRequestSearchQueryValidForCheckout,
        isCheckoutSessionInitializing,
        hasActiveCheckoutSession,
        state.status,
        route.query,
        route.path,
        paymentIntentModelProviderClient,
        isRestoring,
    ]);

    useEffect(() => {
        log.debug("hiero1.5.6 - CheckoutSessionProvider::middle - CHANGED - checkoutSessionState", state);
    }, [state]);

    const updateSessionExpiredAt = useCallback(() => {
        setState({
            ...state,
            expired_at: Date.now() + CheckoutSessionDefaults.SessionTtl,
        });
    }, [state]);

    useEffect(() => {
        if (!isCheckoutSessionInitializing && !!state.payment_type && state.payment_type !== activePaymentType) {
            // upsertCheckoutSession(state);
            setActivePaymentType(state.payment_type);

            // payment type has changed, a new session should be started
            setTimeout(() => reviveSession({ payment_type: state.payment_type, forceNew: true }), 100);
        }
    }, [state.payment_type, isCheckoutSessionInitializing, activePaymentType, reviveSession]);

    useEffect(() => {
        if (!isCheckoutSessionInitializing && !!activePropertyId && !!state.property_id && state.property_id !== activePropertyId) {
            const isCheckoutEntryPageRoute = isRoutePageActive(CheckoutPageKeys.CheckoutEntry);

            // clear the session
            setState(
                defaultEmptyCheckoutSessionStateCreator({
                    visitor_id: visitorId,
                    property_id: activePropertyId,
                    booking_hash: bookingHash,
                })
            );

            // If we are at the checkout item page and the property changes, start a new session.
            // @note - this should normally not be possible, but we want to handle it just in case
            if (isCheckoutEntryPageRoute) {
                reviveSession({ forceNew: true });
            }
        }
    }, [
        activeProperty?.id,
        isCheckoutSessionInitializing,
        activePaymentType,
        reviveSession,
        state.property_id,
        state.started_at,
        bookingHash,
    ]);

    const canEnterCheckoutFlow = useCallback(
        (): boolean => isAvailabilityRequestSearchQueryValidForCheckout,
        [isAvailabilityRequestSearchQueryValidForCheckout]
    );

    const value: ICheckoutSessionContext = React.useMemo(
        () => ({
            state,
            reviveSession,
            canEnterCheckoutFlow,
            isLoading: upsertCheckoutSessionMutator.isLoading || isRestoring,
            upsertCheckoutSession,
            clearCheckoutSession,
            hasActiveCheckoutSession,
            isCheckoutSessionInitializing,
            booking: checkoutBookingValidation.data?.reservedBooking,
            paymentIntentModel: checkoutBookingValidation.data?.paymentIntentModel,
            setState: setCheckoutSessionState,
            updateSessionExpiredAt,
            checkoutBookingValidation,
            isPaymentConfirming,
            setIsPaymentConfirming,
            isRestoringPersistedCheckoutSessionState: isRestoring,
            isPersistingCheckoutSessionState: isPersisting,
        }),
        [
            state,
            reviveSession,
            upsertCheckoutSessionMutator.isLoading,
            hasActiveCheckoutSession,
            isCheckoutSessionInitializing,
            clearCheckoutSession,
            upsertCheckoutSession,
            checkoutBookingValidation.data?.reservedBooking,
            checkoutBookingValidation.data?.paymentIntentModel,
            setCheckoutSessionState,
            updateSessionExpiredAt,
            checkoutBookingValidation,
            isPaymentConfirming,
            setIsPaymentConfirming,
            isRestoring,
            isPersisting,
        ]
    );

    return (
        <CheckoutSessionContext.Provider value={value}>
            {isCheckoutSessionInitializing ? null : typeof children === "function" ? children(value) : children}
        </CheckoutSessionContext.Provider>
    );
}
